import React from 'react';
import { Table, TableScroll, Badge, SortableTh, RowActions, ACTIONS_COL_WIDTH, ServiceAreaPill, EmptyValue, ClampText } from '@/components/ui';
import { Asset, AssetStatus } from '../../../types/Asset';
import { ASSET_TYPE_LABELS, ASSET_TYPES } from '../../../constants/assetTypes';

interface AssetsTableProps {
  assets: Asset[];
  onRowClick: (asset: Asset) => void;
  onDelete?: (assetId: number) => void;
  onToggleStatus?: (assetId: number) => void;
  onShowHistory?: (asset: Asset) => void;
  sortBy?: string | null;
  loading?: boolean;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const AssetsTable: React.FC<AssetsTableProps> = ({ assets, onRowClick, onDelete, onToggleStatus, onShowHistory, sortBy = null, sortDir = 'asc', onSort, loading = false }) => {
  // RowActions stops row-click propagation for the whole cell, so these no longer need wrappers.

  const getAssetTypeLabel = (assetType: string): string => {
    // Map AssetType values to ASSET_TYPES values
    const typeMapping: Record<string, ASSET_TYPES> = {
      'road': ASSET_TYPES.ROAD,
      'culvert': ASSET_TYPES.CULVERT,
      'sign': ASSET_TYPES.SIGN,
      'cattleguard': ASSET_TYPES.CATTLEGUARD, 
      'restarea': ASSET_TYPES.RESTAREA, 
      'structure': ASSET_TYPES.STRUCTURE 
    };

    const mappedType = typeMapping[assetType];
    if (mappedType && ASSET_TYPE_LABELS[mappedType]) {
      return ASSET_TYPE_LABELS[mappedType];
    }
    
    // Fallback to the original asset type if no mapping found
    return assetType;
  };

  const hasActions = !!(onDelete || onToggleStatus || onShowHistory);
  const columnCount = hasActions ? 12 : 11;

  return (
    // TableScroll is the page's one scroll box (both axes): the sticky header and the pinned
    // Asset Number / Actions columns attach to it (W-H5, W-M8).
    <TableScroll>
      <Table hover stickyHeader>
        <thead>
          <tr>
            <SortableTh label="Asset Number" sortKey="assetNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} className="wrap-pin-left wrap-pin-edge" />
            <SortableTh label="Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ minWidth: '14rem' }} />
            <SortableTh label="Type" sortKey="assetType" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Status" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Subtype" sortKey="subtype" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Description" sortKey="description" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ minWidth: '18rem' }} />
            <th>Service Area</th>
            <SortableTh label="Contract Area" sortKey="contractArea" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Road Length (km)" sortKey="length" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Winter Class" sortKey="winterClass" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Summer Class" sortKey="summerClass" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            {(onDelete || onToggleStatus || onShowHistory) && (
              <th style={{ width: ACTIONS_COL_WIDTH }} className="wrap-pin-right tw-text-right">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr key={asset.id} onClick={() => onRowClick(asset)} style={{ cursor: 'pointer' }}>
              <td className="wrap-pin-left wrap-pin-edge">{asset.assetNumber}</td>
              <td>{asset.name ? <ClampText>{asset.name}</ClampText> : <EmptyValue />}</td>
              <td>
                <Badge pill bg="info">
                  {getAssetTypeLabel(asset.assetType)}
                </Badge>
              </td>
              <td>
                <Badge pill bg={asset.status === AssetStatus.Disabled ? 'secondary' : 'success'}>
                  {asset.status || AssetStatus.Active}
                </Badge>
              </td>
              <td>
                {asset.subtype ? <Badge pill bg="secondary">{asset.subtype}</Badge> : <EmptyValue />}
              </td>
              <td>{asset.description ? <ClampText>{asset.description}</ClampText> : <EmptyValue />}</td>
              <td>
                {asset.contractArea?.foremanAreas && asset.contractArea.foremanAreas.length > 0 ? (
                  // One single-line pill per area, compact form (W-L5): never "Service Area / 22".
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {Array.from(new Set(asset.contractArea.foremanAreas.map(fa => fa.serviceArea?.name).filter(Boolean))).map((serviceAreaName, index) => (
                      <ServiceAreaPill key={index} value={serviceAreaName as string} compact />
                    ))}
                  </div>
                ) : <EmptyValue />}
              </td>
              <td>
                {/* Contract Area is plain text, not a pill (W-M5). */}
                {asset.contractArea?.name || <EmptyValue />}
              </td>
              <td>{asset.assetType === 'road' && asset.length != null ? `${asset.length.toFixed(2)} km` : <EmptyValue />}</td>
              <td>{(asset.assetType === 'road' && asset.winterClass) || <EmptyValue />}</td>
              <td>{(asset.assetType === 'road' && asset.summerClass) || <EmptyValue />}</td>
              {(onDelete || onToggleStatus || onShowHistory) && (
                <td className="wrap-pin-right">
                  <RowActions
                    actions={[
                      {
                        kind: 'history',
                        label: 'Asset History',
                        onClick: () => onShowHistory?.(asset),
                        hidden: !onShowHistory,
                      },
                      asset.status === AssetStatus.Disabled
                        ? {
                            kind: 'enable',
                            label: 'Enable Asset',
                            onClick: () => onToggleStatus?.(asset.id),
                            hidden: !onToggleStatus,
                          }
                        : {
                            kind: 'disable',
                            label: 'Disable Asset',
                            onClick: () => onToggleStatus?.(asset.id),
                            hidden: !onToggleStatus,
                          },
                      {
                        kind: 'delete',
                        label: 'Delete Asset',
                        onClick: () => onDelete?.(asset.id),
                        hidden: !onDelete,
                      },
                    ]}
                  />
                </td>
              )}
            </tr>
          ))}
          {assets.length === 0 && (
            <tr>
              <td colSpan={columnCount} className="tw-py-6 tw-text-center tw-text-content-muted">
                {loading ? 'Loading assets...' : 'No assets found'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default AssetsTable;